import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { QrCodeIcon } from '@icons';

import { Button, Modal, Spinner, Tooltip } from '@components';
import { AccountType, OrgStatus } from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { useLazyGetLeadPaymentOrderDetailsQuery } from '@features/lead-management/queries/LeadManagementQuery';
import { useLazyGetPaymentDetailsByIdQuery } from '@features/transactions/queires/TransactionsQuery';
import useCheckPermission from '@hooks/checkPermission';
import { MemberTransactionType } from '@types';
import { getLocalStorageItem } from '@utils/storage';

import QuickCollectQrModal from './QuickCollectQrModal';
import TransactionDetails from './TransactionDetails';

interface PaymentDetailsProps {
  isOpen: boolean;
  handleCancel: () => void;
  branchId: string;
  orderId: string;
  handleDownload: () => void;
  leadId?: string;
  /** V2 analytics only - not fired unless the caller opts in. */
  onQrCodeViewed?: (details: MemberTransactionType) => void;
  /** V2 analytics only - not fired unless the caller opts in. */
  onFeeSummaryExpanded?: (details: MemberTransactionType) => void;
}

const PaymentDetails = ({
  isOpen,
  handleCancel,
  branchId,
  orderId,
  handleDownload,
  leadId,
  onQrCodeViewed,
  onFeeSummaryExpanded
}: PaymentDetailsProps) => {
  const { currentOrg } = useSelector((state: any) => state.user);
  const { checkPermission } = useCheckPermission();

  const hasDownloadReceiptAccess = checkPermission(
    leadId
      ? [
          UserPermissionTypes.PAYMENT_ORDER_GENERATE_RECEIPT,
          UserPermissionTypes.LEAD_REPORT_GENERATE
        ]
      : [UserPermissionTypes.PAYMENT_ORDER_GENERATE_RECEIPT]
  );

  const [paymentDetails, setPaymentDetails] = useState<MemberTransactionType>();

  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );

  const [
    getPaymentDetailsById,
    {
      isLoading: paymentDetailsLoading,
      isFetching: paymentDetailsFetching,
      isUninitialized: paymentDetailsUninitialized
    }
  ] = useLazyGetPaymentDetailsByIdQuery();

  const [
    getLeadPaymentOrderDetails,
    {
      isLoading: leadPaymentDetailsLoading,
      isFetching: leadPaymentDetailsFetching,
      isUninitialized: leadPaymentDetailsUninitialized
    }
  ] = useLazyGetLeadPaymentOrderDetailsQuery();

  const handleGetPaymentDetailsById = async () => {
    if (leadId) {
      const res = await getLeadPaymentOrderDetails({
        organizationId,
        leadId,
        branchId,
        paymentOrderId: orderId
      });
      if ('data' in res) {
        setPaymentDetails(res.data?.data || res.data);
      }
    } else {
      const res = await getPaymentDetailsById({
        params: { organizationId, branchId, orderId }
      });
      if ('data' in res) {
        setPaymentDetails(res.data);
      }
    }
  };

  const [isQrCodeOpen, setIsQrCodeOpen] = useState(false);

  const handleOpenModal = () => {
    setIsQrCodeOpen(true);
    if (paymentDetails) onQrCodeViewed?.(paymentDetails);
  };

  const handleCloseModal = () => {
    setIsQrCodeOpen(false);
  };

  const isLoadingPaymentDetails = leadId
    ? leadPaymentDetailsLoading ||
      leadPaymentDetailsFetching ||
      leadPaymentDetailsUninitialized
    : paymentDetailsLoading ||
      paymentDetailsFetching ||
      paymentDetailsUninitialized;

  const isOrgLite =
    (currentOrg?.status === OrgStatus.KYC_PENDING ||
      currentOrg?.status === OrgStatus.KYC_SUBMITTED) &&
    currentOrg?.type === AccountType.ORGANIZATION &&
    !currentOrg?.is_kyc_deferred;

  const isIndividualLite =
    (currentOrg?.status === OrgStatus.KYC_PENDING ||
      currentOrg?.status === OrgStatus.KYC_SUBMITTED) &&
    currentOrg?.type === AccountType.INDIVIDUAL &&
    !currentOrg?.is_kyc_deferred;

  const showQrCode =
    paymentDetails?.status === 'processing' &&
    !isOrgLite &&
    !isIndividualLite &&
    !paymentDetails?.is_auto_debit_in_progress;

  const isProcessingPayment = paymentDetails?.status === 'success';

  useEffect(() => {
    handleGetPaymentDetailsById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onCancel={handleCancel}
      isCloseIconRequired
      closeIconClassName={`!p-0 !mr-3 ${isProcessingPayment ? '!pb-4' : ''}`}
    >
      <div
        className={`flex max-h-[90vh] min-h-0 w-118 flex-col overflow-hidden rounded-lg bg-white pb-8 pt-4 ${
          isQrCodeOpen ? 'hidden' : ''
        }`}
      >
        <div className="shrink-0">
          <div className="flex items-center justify-between gap-3 border-b border-cofeeGreyBlue200 pb-2 pl-6 pr-14">
            <div className="min-w-0 flex-1 text-xl font-semibold">
              Payment Details
            </div>
            {showQrCode && (
              <Button
                variant="transparent"
                handleClick={handleOpenModal}
                className="inline-flex shrink-0 items-center gap-1 !p-0"
                data-tooltip-id="qr-code-icon"
              >
                <QrCodeIcon />
                <span className="whitespace-nowrap text-sm font-normal">
                  Scan QR Code
                </span>
              </Button>
            )}
          </div>
          <Tooltip id="qr-code-icon" type="text" tooltipText="Show QR Code" />
        </div>

        {isLoadingPaymentDetails ? (
          <div className="flex min-h-48 shrink-0 items-center justify-center px-6 py-10">
            <Spinner />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mt-6 min-h-0 flex-1 overflow-y-auto">
              {paymentDetails && (
                <TransactionDetails
                  memberDetails={paymentDetails}
                  branchId={branchId}
                  orderId={orderId}
                  // paymentStatus={paymentDetails?.status}
                  // paymentSettlementStatus={paymentDetails?.settlement_status}
                  leadId={leadId}
                  onFeeSummaryExpanded={
                    onFeeSummaryExpanded
                      ? () => onFeeSummaryExpanded(paymentDetails)
                      : undefined
                  }
                />
              )}
            </div>
            {paymentDetails?.total_paid_amount !== '0' && (
              <div className="mt-4 shrink-0">
                <div className="border-t border-cofeeGreyBlue200" />
                <div className="flex justify-end px-6 pt-4">
                  <div
                    className={`flex items-center justify-between gap-2 ${
                      hasDownloadReceiptAccess ? 'w-1/2' : 'w-auto'
                    }`}
                  >
                    <Button
                      label="Cancel"
                      size={hasDownloadReceiptAccess ? 'full' : 'medium'}
                      variant="tertiary"
                      className="h-[44px]"
                      handleClick={handleCancel}
                    />
                    {hasDownloadReceiptAccess && (
                      <Button
                        label="Download"
                        size="full"
                        variant="primary"
                        className="h-[44px]"
                        handleClick={handleDownload}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {isQrCodeOpen && (
        <QuickCollectQrModal
          isOpen={isQrCodeOpen}
          paymentDetails={paymentDetails || ({} as MemberTransactionType)}
          onClose={handleCloseModal}
        />
      )}
    </Modal>
  );
};

export default PaymentDetails;
